import { useState } from 'react';
import { euro } from '@fleet/shared';

/**
 * Line chart as inline SVG, same approach as `RingCircle`, no chart library.
 * Points carry their own x-position on a shared 0..1 scale so a caller can plot
 * a measured series and a modelled line on one time axis.
 *
 * A solid line draws itself in on mount (dash-offset run down to zero, which is
 * why each path gets its own measured length); a dashed one fades in instead,
 * because the dash pattern is already using that attribute.
 *
 * With `readout` the chart gets the same detail row as the bar charts: invisible
 * hit bands, one per point and the full height of the plot, so a 4px dot is still
 * easy to hit on a phone. Hover or focus scrubs, a tap parks, and with nothing
 * picked it falls back to `standaardIndex`, or to the last point, so there is
 * always a value on screen.
 */
export interface LijnPunt {
  /** Position on the x-axis, 0 = left edge, 1 = right edge. */
  x: number;
  y: number;
  label?: string;
}

export interface Lijn {
  key: string;
  label: string;
  color: string;
  punten: LijnPunt[];
  /** Dashed, for a modelled line as opposed to a measured one. */
  gestippeld?: boolean;
  /** Draw a dot per point, useful for sparse measurements. */
  punten_zichtbaar?: boolean;
  /** Tint the area under the line, for the one series that carries the story. */
  vlak?: boolean;
}

/** Path length in user units, so the draw-in animation runs at an even pace. */
function lengte(punten: readonly { x: number; y: number }[]): number {
  let l = 0;
  for (let i = 1; i < punten.length; i++) {
    const a = punten[i - 1]!;
    const b = punten[i]!;
    l += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return Math.max(1, Math.round(l));
}

/**
 * `leeg` renders the frame faded with a caption instead of hiding the chart, so
 * an empty series still shows what would appear there once data arrives.
 */
export function LineChart({
  lijnen,
  hoogte = 200,
  eenheid = 'euro',
  xLabels = [],
  markerX,
  markerLabel,
  leeg = false,
  leegLabel = 'geen data',
  readout = false,
  standaardIndex,
}: {
  lijnen: readonly Lijn[];
  hoogte?: number;
  eenheid?: 'euro' | 'km';
  xLabels?: readonly string[];
  /** Vertical marker at this 0..1 position, e.g. "today". */
  markerX?: number;
  markerLabel?: string;
  leeg?: boolean;
  leegLabel?: string;
  /** Show a detail row under the chart and make the points hoverable/tappable. */
  readout?: boolean;
  /** Point the readout starts on; defaults to the last one. */
  standaardIndex?: number;
}) {
  const [actief, setActief] = useState<number | null>(null);
  const W = 1000;
  const H = 300;
  const pad = { l: 4, r: 4, t: 16, b: 6 };
  const alle = lijnen.flatMap((l) => l.punten.map((p) => p.y));
  const maxY = Math.max(1, ...alle);
  // Een km-reeks begint niet bij nul: dan is de lijn een vlakke streep bovenaan.
  const minY = eenheid === 'km' && alle.length ? Math.min(...alle) * 0.98 : 0;
  const spanY = Math.max(1, maxY - minY);
  const px = (x: number) => pad.l + x * (W - pad.l - pad.r);
  const py = (y: number) => H - pad.b - ((y - minY) / spanY) * (H - pad.t - pad.b);
  const fmt = (n: number) =>
    eenheid === 'euro' ? euro(n) : `${Math.round(n).toLocaleString('nl-NL')} km`;

  // De gemeten reeks draagt de detailregel; een gemodelleerde lijn heeft geen punten.
  const meet = lijnen.find((l) => l.punten_zichtbaar) ?? lijnen[0];
  const meetbaar = readout && !leeg && meet && meet.punten.length > 1;
  const standaard =
    standaardIndex != null && standaardIndex >= 0 && meet && standaardIndex < meet.punten.length
      ? standaardIndex
      : (meet?.punten.length ?? 0) - 1;
  const idx = meetbaar ? (actief != null && actief < meet.punten.length ? actief : standaard) : -1;
  const gekozen = idx >= 0 ? meet!.punten[idx]! : null;
  const vorige = idx > 0 ? meet!.punten[idx - 1]! : null;

  return (
    <div style={{ opacity: leeg ? 0.3 : 1, position: 'relative', transition: 'opacity .2s ease' }}>
      {!leeg && lijnen.length > 1 && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
          {lijnen.map((l) => (
            <span
              key={l.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11.5,
                fontWeight: 700,
                color: '#666666',
              }}
            >
              <span
                style={{
                  width: 16,
                  height: 0,
                  borderTop: `2px ${l.gestippeld ? 'dashed' : 'solid'} ${l.color}`,
                }}
              />
              {l.label}
            </span>
          ))}
        </div>
      )}

      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={hoogte}
        style={{ display: 'block', overflow: 'visible' }}
      >
        {/* horizontale hulplijnen */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <line
            key={f}
            x1={px(0)}
            y1={py(minY + spanY * f)}
            x2={px(1)}
            y2={py(minY + spanY * f)}
            stroke="#EDE4D8"
            strokeWidth="1"
          />
        ))}
        <text x={px(0)} y={py(maxY) - 5} fontSize="12" fontWeight="700" fill="#999999">
          {fmt(maxY)}
        </text>
        {minY > 0 && (
          <text x={px(0)} y={py(minY) - 5} fontSize="12" fill="#BBBBBB">
            {fmt(minY)}
          </text>
        )}

        {markerX != null && (
          <g style={{ animation: 'vsFadeIn .4s ease .5s both' }}>
            <line
              x1={px(markerX)}
              y1={pad.t}
              x2={px(markerX)}
              y2={H - pad.b}
              stroke="#B0543B"
              strokeWidth="1.5"
              strokeDasharray="4 3"
            />
            {markerLabel && (
              <text x={px(markerX) + 5} y={pad.t} fontSize="12" fill="#B0543B" fontWeight="700">
                {markerLabel}
              </text>
            )}
          </g>
        )}

        {lijnen.map((l, li) => {
          if (!l.punten.length) return null;
          const xy = l.punten.map((p) => ({ x: px(p.x), y: py(p.y) }));
          const d = xy
            .map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
            .join(' ');
          const len = lengte(xy);
          const bodem = H - pad.b;
          return (
            <g key={l.key}>
              {l.vlak && xy.length > 1 && (
                <path
                  d={`${d} L${xy[xy.length - 1]!.x.toFixed(1)} ${bodem} L${xy[0]!.x.toFixed(1)} ${bodem} Z`}
                  fill={l.color}
                  opacity="0.1"
                  style={{ animation: 'vsFadeIn .5s ease .35s both' }}
                />
              )}
              <path
                d={d}
                fill="none"
                stroke={l.color}
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
                {...(l.gestippeld
                  ? { strokeDasharray: '6 4', style: { animation: 'vsFadeIn .5s ease .3s both' } }
                  : {
                      strokeDasharray: len,
                      style: {
                        // De keyframe leest de lengte hieruit, zodat elke lijn even snel loopt.
                        ['--vs-len' as string]: String(len),
                        animation: `vsDraw .8s cubic-bezier(.4,0,.2,1) ${li * 0.1}s both`,
                      } as React.CSSProperties,
                    })}
              />
              {l.punten_zichtbaar &&
                xy.map((p, i) => (
                  <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={l === meet && i === idx ? 6.5 : 4}
                    fill={l === meet && i === idx ? l.color : '#FFFFFF'}
                    stroke={l.color}
                    strokeWidth="2.5"
                    style={{
                      transformOrigin: `${p.x}px ${p.y}px`,
                      animation: `vsPop .3s cubic-bezier(.4,0,.2,1) ${0.5 + (i / xy.length) * 0.5}s both`,
                    }}
                  >
                    <title>
                      {l.punten[i]!.label
                        ? `${l.punten[i]!.label}: ${fmt(l.punten[i]!.y)}`
                        : fmt(l.punten[i]!.y)}
                    </title>
                  </circle>
                ))}
            </g>
          );
        })}
        {meetbaar && gekozen && (
          <line
            x1={px(gekozen.x)}
            y1={pad.t}
            x2={px(gekozen.x)}
            y2={H - pad.b}
            stroke="#111111"
            strokeWidth="1"
            strokeDasharray="3 3"
            opacity="0.3"
          />
        )}

        {/* Onzichtbare banen over de volle hoogte: op een telefoon is een punt van
            4 px niet te raken, een baan van de halve afstand tot de buren wel. */}
        {meetbaar &&
          meet!.punten.map((p, i) => {
            const hier = px(p.x);
            const links = i === 0 ? 0 : (px(meet!.punten[i - 1]!.x) + hier) / 2;
            const rechts =
              i === meet!.punten.length - 1 ? W : (hier + px(meet!.punten[i + 1]!.x)) / 2;
            return (
              <rect
                key={`baan-${i}`}
                x={links}
                y={0}
                width={Math.max(1, rechts - links)}
                height={H}
                fill="transparent"
                pointerEvents="all"
                tabIndex={0}
                role="button"
                aria-label={`${p.label ?? ''} ${fmt(p.y)}`}
                style={{ cursor: 'pointer', outline: 'none' }}
                onPointerEnter={() => setActief(i)}
                onPointerLeave={(e) => {
                  if (e.pointerType === 'mouse') setActief(null);
                }}
                onFocus={() => setActief(i)}
                onClick={() => setActief(i)}
              />
            );
          })}
      </svg>

      {xLabels.length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11,
            fontWeight: 700,
            color: '#888888',
            marginTop: 4,
          }}
        >
          {xLabels.map((x, i) => (
            <span key={i}>{x}</span>
          ))}
        </div>
      )}

      {meetbaar && gekozen && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid #E3D9CC',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: '6px 14px',
            minHeight: 40,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 900, color: '#111111' }}>{gekozen.label}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#2A6085' }}>{fmt(gekozen.y)}</span>
          {vorige && (
            <span style={{ fontSize: 12, color: '#666666' }}>
              {`+${fmt(gekozen.y - vorige.y)} sinds ${vorige.label ?? 'de vorige meting'}`}
            </span>
          )}
        </div>
      )}

      {leeg && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            fontSize: 13,
            fontWeight: 700,
            color: '#888888',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          {leegLabel}
        </div>
      )}
    </div>
  );
}
